import React, { useEffect, useMemo, useState } from 'react';
import ModelSelect from './ModelSelect';
import {
  createResearchProject,
  updateResearchProject,
  rerunResearchJob,
} from '../utils/api';
import './ResearchProjectModal.css';

const EMPTY = {
  title: '',
  prompt: '',
  mode: 'general_report',
  provider: 'anthropic',
  model: '',
  maxSteps: 125,
  maxSources: 40,
  options: {},
};

function researchProviderLabel(providerId) {
  if (providerId === 'openai') return 'OpenAI';
  if (providerId === 'anthropic') return 'Anthropic';
  if (providerId === 'zagger') return 'Zagger research';
  return providerId;
}

export default function ResearchProjectModal({
  open,
  mode = 'create',
  initial = EMPTY,
  meta = {},
  orgId = null,
  sourceJobId = null,
  onClose,
  onSaved,
}) {
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [researchMode, setResearchMode] = useState('general_report');
  const [provider, setProvider] = useState('anthropic');
  const [model, setModel] = useState('');
  const [maxSteps, setMaxSteps] = useState(125);
  const [maxSources, setMaxSources] = useState(40);
  const [forceZaggerWebSearch, setForceZaggerWebSearch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const modes = useMemo(() => {
    const list = meta?.modes?.length ? meta.modes : ['general_report'];
    return list.map((m) => ({ id: m, label: m.replace(/_/g, ' ') }));
  }, [meta]);

  const isRerun = mode === 'rerun';
  const showZaggerSearchToggle = provider === 'openai' || provider === 'anthropic';

  useEffect(() => {
    if (!open) return;
    const defaults = meta?.defaults || {};
    const defaultProvider = initial.provider || defaults.provider || 'anthropic';
    const providerModels = meta?.providerDefaultModels || {};
    const initialOpts = initial.options || {};
    setTitle(initial.title || '');
    setPrompt(initial.prompt || '');
    setResearchMode(initial.mode || defaults.mode || 'general_report');
    setProvider(defaultProvider);
    setModel(initial.model || providerModels[defaultProvider] || defaults.model || '');
    setMaxSteps(initial.maxSteps || defaults.maxSteps || 125);
    setMaxSources(initial.maxSources || defaults.maxSources || 40);
    setForceZaggerWebSearch(
      initialOpts.forceZaggerWebSearch === true
      || initialOpts.useZaggerWebSearch === true
      || defaults.forceZaggerWebSearch === true
    );
    setError('');
  }, [open, initial, meta]);

  if (!open) return null;

  const buildPayload = (run) => {
    const options = { ...(initial.options || {}) };
    if (showZaggerSearchToggle) {
      options.forceZaggerWebSearch = !!forceZaggerWebSearch;
    } else {
      delete options.forceZaggerWebSearch;
    }
    const payload = {
      title: title.trim(),
      prompt: prompt.trim(),
      mode: researchMode,
      provider,
      model: model || meta?.providerDefaultModels?.[provider] || meta?.defaults?.model,
      maxSteps,
      maxSources,
      options,
      run,
    };
    // Re-run keeps the source job's org/owner; don't send the acting user's suffix.
    if (!isRerun && orgId != null && orgId !== '') {
      payload.orgId = orgId;
    }
    return payload;
  };

  const handleSave = async (run) => {
    if (!title.trim() || !prompt.trim()) {
      setError('Title and objective are required');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = buildPayload(run);
      let result;
      if (isRerun) {
        if (!sourceJobId) {
          throw new Error('Missing source job for re-run');
        }
        if (!run) {
          throw new Error('Re-run always starts a new job');
        }
        const rerun = await rerunResearchJob(sourceJobId, payload);
        result = { project: null, job: rerun.job };
      } else if (mode === 'edit' && initial.id) {
        result = await updateResearchProject(initial.id, payload);
      } else {
        result = await createResearchProject(payload);
      }
      await onSaved?.({ ...result, ran: run || isRerun });
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save research project');
    } finally {
      setSubmitting(false);
    }
  };

  const heading = isRerun
    ? 'Re-run research'
    : mode === 'edit'
      ? 'Edit research project'
      : 'New research project';
  const subtitle = isRerun
    ? 'Edit parameters and start a new run. Does not change the saved project. Re-runs as the original job owner so their API keys are used.'
    : 'Save settings for this research topic. Edit parameters before each run.';

  return (
    <div className="research-config-overlay" role="presentation">
      <div
        className="research-config-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="research-config-title"
      >
        <header className="research-config-header">
          <h2 id="research-config-title">{heading}</h2>
          <p>{subtitle}</p>
        </header>

        <div className="research-config-body">
          {error ? <div className="research-config-error" role="alert">{error}</div> : null}

          <label className="research-config-label">
            Title
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Q3 competitive landscape"
              required
            />
          </label>

          <label className="research-config-label">
            Objective
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what you want researched..."
              rows={5}
              required
            />
          </label>

          <div className="research-config-row">
            <label className="research-config-label">
              Mode
              <select value={researchMode} onChange={(e) => setResearchMode(e.target.value)}>
                {modes.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </label>

            <label className="research-config-label">
              Provider
              <select
                value={provider}
                onChange={(e) => {
                  const next = e.target.value;
                  setProvider(next);
                  const nextModel = meta?.providerDefaultModels?.[next];
                  if (nextModel) setModel(nextModel);
                }}
              >
                {(meta?.providers || ['anthropic', 'openai', 'zagger']).map((p) => (
                  <option key={p} value={p}>{researchProviderLabel(p)}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="research-config-label">
            Model
            <ModelSelect
              value={model}
              onChange={setModel}
              providerFilter={provider === 'zagger' ? null : provider}
              placeholder="Select model"
            />
          </label>

          <div className="research-config-row">
            <label className="research-config-label">
              Max steps
              <input
                type="number"
                min={3}
                max={500}
                value={maxSteps}
                onChange={(e) => setMaxSteps(Number(e.target.value) || 125)}
              />
            </label>

            <label className="research-config-label">
              Max sources
              <input
                type="number"
                min={5}
                max={80}
                value={maxSources}
                onChange={(e) => setMaxSources(Number(e.target.value) || 40)}
              />
            </label>
          </div>

          {showZaggerSearchToggle ? (
            <label className="research-config-check">
              <input
                type="checkbox"
                checked={forceZaggerWebSearch}
                onChange={(e) => setForceZaggerWebSearch(e.target.checked)}
              />
              <span>
                Use Zagger web search
                <small>
                  Prefer Brave/Serper/Tavily tools instead of provider-native web_search.
                  Helps avoid long OpenAI Responses waits (headers timeouts with gpt-5).
                  Requires a key under Settings → User → Web Search.
                  Native OpenAI search (when unchecked) uses high search context.
                </small>
              </span>
            </label>
          ) : null}
        </div>

        <footer className="research-config-footer">
          <button type="button" className="research-config-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          {mode === 'edit' && !isRerun ? (
            <button
              type="button"
              className="research-config-btn"
              onClick={() => handleSave(false)}
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
          ) : null}
          <button
            type="button"
            className="research-config-btn research-config-btn--primary"
            onClick={() => handleSave(true)}
            disabled={submitting}
          >
            {submitting ? 'Starting…' : isRerun ? 'Re-run' : 'Save & run'}
          </button>
        </footer>
      </div>
    </div>
  );
}
